import './App.css';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';

import { AuthProvider } from './auth/AuthProvider';
import { useAuth } from './auth/context';
import { Header } from './Header';
import { TopBar } from './TopBar';
import { Wizard } from './Wizard';
import { Dashboard } from './routes/Dashboard';
import { AudienceView } from './routes/newsletter/AudienceView';
import { BasicsView } from './routes/newsletter/BasicsView';
import { DesignStudio } from './routes/newsletter/DesignStudio';
import { KeyedNewsletterWorkspace } from './routes/newsletter/workspace';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { ResetPassword } from './routes/ResetPassword';
import { SignIn } from './routes/SignIn';
import { SignUp } from './routes/SignUp';
import { mockSuggestionProvider } from './suggestions/mock-provider';
import type { SuggestionProvider } from './suggestions/provider';

export interface AppProps {
  /**
   * The suggestions backend, injected at the composition root. Defaults to the
   * deterministic mock; swap in a Bedrock-backed provider here with no other
   * change to the app.
   */
  readonly provider?: SuggestionProvider;
}

/**
 * The marketing/auth chrome: the simple brand Header over the wizard and the
 * sign-in/sign-up screens. Signed-out visitors live here; the platform proper uses
 * the {@link AppLayout} top bar instead.
 */
function PublicLayout(): React.JSX.Element {
  return (
    <div className="app">
      <Header />
      <main className="app__main">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * The authenticated PLATFORM chrome: the persistent {@link TopBar} (logo · newsletter
 * switcher · account) above every /app screen. It replaces the marketing Header once
 * the owner is inside the app, so the newsletter name/switcher and account live in one
 * consistent bar rather than being repeated per screen.
 */
function AppLayout(): React.JSX.Element {
  return (
    <div className="app">
      <TopBar />
      <main className="app__main">
        <Outlet />
      </main>
    </div>
  );
}

/**
 * The catch-all redirect for an unknown path. A signed-in owner is sent to their
 * dashboard (/app) rather than being bounced out to the marketing wizard at "/",
 * which would read as a sign-out; a signed-out visitor still lands on the wizard.
 */
function NotFoundRedirect(): React.JSX.Element {
  const { status } = useAuth();
  return <Navigate to={status === 'signedIn' ? '/app' : '/'} replace />;
}

export function App({ provider = mockSuggestionProvider }: AppProps): React.JSX.Element {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public + auth: the marketing Header chrome. */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Wizard provider={provider} />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/reset" element={<ResetPassword />} />
          </Route>

          {/* Authenticated platform: the persistent top bar over every /app screen. */}
          <Route
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/newsletters/:newsletterId" element={<KeyedNewsletterWorkspace />}>
              {/* STUDIO (index): the editor. AUDIENCE: subscribers + sending.
                  BASICS: the full setup editor, schedule, and delete. */}
              <Route index element={<DesignStudio />} />
              <Route path="audience" element={<AudienceView />} />
              <Route path="basics" element={<BasicsView />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
